import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import type {
    PatientConflictAction,
    PatientConflictState,
} from '~/hooks/patient/usePatientConflictPrompt';

const TITLE_ID = 'multi-patient-conflict-dialog-title';
const WARNING_ID = 'multi-patient-conflict-dialog-warning';

const ID_SX = {
    fontFamily: 'fontFamilyMonospace',
    fontWeight: 500,
    color: 'text.primary',
    wordBreak: 'break-all',
    whiteSpace: 'pre-line',
} as const;

interface MultiPatientConflictDialogProps {
    conflict: PatientConflictState;
    onAction: (action: PatientConflictAction) => void;
}

/**
 * Warns that incoming DICOM data belongs to a different patient than the
 * loaded one, shows both patient IDs, and asks how to continue.
 *
 * Discarding the incoming data is the default: it is focused, and Escape or
 * a backdrop click also choose it. Switching patient, or loading both
 * (outside clinical mode only), stays disabled until the user acknowledges
 * that the data belongs to a different patient; each opening asks for the
 * acknowledgement again. Incoming data for several patients can only be
 * discarded.
 */
export default function MultiPatientConflictDialog({
    conflict,
    onAction,
}: MultiPatientConflictDialogProps) {
    const {
        open,
        loadedPatientIds,
        incomingPatientIds,
        hasUnsavedChanges,
        clinicalUse,
    } = conflict;
    const [acknowledged, setAcknowledged] = useState(false);
    const discardRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (open) {
            setAcknowledged(false);
        }
    }, [open]);

    const singleIncoming = incomingPatientIds.length === 1;
    const canLoadBoth = singleIncoming && !clinicalUse;

    return (
        <Dialog
            open={open}
            onClose={() => onAction('discard')}
            aria-labelledby={TITLE_ID}
            aria-describedby={WARNING_ID}
            fullWidth
            maxWidth="sm"
            PaperProps={{ elevation: 0 }}
            // A menu closing as the dialog opens can take focus back after
            // `autoFocus` ran, so discard claims it once the dialog is open.
            TransitionProps={{ onEntered: () => discardRef.current?.focus() }}
        >
            <DialogTitle id={TITLE_ID}>
                <FormattedMessage id="patientConflict.title" />
            </DialogTitle>
            <DialogContent>
                <Alert
                    id={WARNING_ID}
                    severity="warning"
                    data-testid="patient-conflict-warning"
                >
                    <FormattedMessage
                        id="patientConflict.warning"
                        values={{ count: loadedPatientIds.length }}
                    />
                    {/* Whole IDs, one per line in a fixed-width face, so they
                        can be compared character by character. */}
                    <Box
                        component="dl"
                        display="grid"
                        gridTemplateColumns="auto 1fr"
                        columnGap={2}
                        rowGap={0.5}
                        alignItems="baseline"
                        mt={1.5}
                        mb={0}
                    >
                        <Typography component="dt" variant="body2">
                            <FormattedMessage
                                id="patientConflict.loadedPatientId"
                                values={{ count: loadedPatientIds.length }}
                            />
                        </Typography>
                        <Typography
                            component="dd"
                            m={0}
                            sx={ID_SX}
                            data-testid="patient-conflict-loaded-id"
                        >
                            <SensitiveInfoTypography>
                                {loadedPatientIds.join('\n')}
                            </SensitiveInfoTypography>
                        </Typography>
                        <Typography component="dt" variant="body2">
                            <FormattedMessage
                                id="patientConflict.incomingPatientId"
                                values={{ count: incomingPatientIds.length }}
                            />
                        </Typography>
                        <Typography
                            component="dd"
                            m={0}
                            sx={ID_SX}
                            data-testid="patient-conflict-incoming-id"
                        >
                            <SensitiveInfoTypography>
                                {incomingPatientIds.join('\n')}
                            </SensitiveInfoTypography>
                        </Typography>
                    </Box>
                </Alert>
                <DialogContentText variant="body2" mt={2}>
                    {!singleIncoming && (
                        <FormattedMessage id="patientConflict.messageSeveralIncoming" />
                    )}
                    {singleIncoming && clinicalUse && (
                        <FormattedMessage id="patientConflict.message" />
                    )}
                    {canLoadBoth && (
                        <FormattedMessage
                            id="patientConflict.messageNonClinical"
                            values={{ count: loadedPatientIds.length }}
                        />
                    )}
                </DialogContentText>
                {hasUnsavedChanges && singleIncoming && (
                    <Alert
                        severity="error"
                        sx={{ mt: 2 }}
                        data-testid="unsaved-changes-warning"
                    >
                        <FormattedMessage id="patientConflict.unsavedWarning" />
                    </Alert>
                )}
                {singleIncoming && (
                    <FormControlLabel
                        sx={{ mt: 1 }}
                        control={
                            <Checkbox
                                checked={acknowledged}
                                onChange={(event) =>
                                    setAcknowledged(event.target.checked)
                                }
                                data-testid="conflict-dialog-acknowledge"
                            />
                        }
                        slotProps={{ typography: { variant: 'body2' } }}
                        label={
                            <FormattedMessage id="patientConflict.acknowledge" />
                        }
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => onAction('discard')}
                    variant="contained"
                    autoFocus
                    ref={discardRef}
                    data-testid="conflict-dialog-discard"
                >
                    <FormattedMessage id="patientConflict.discardIncoming" />
                </Button>
                {canLoadBoth && (
                    <Button
                        onClick={() => onAction('loadBoth')}
                        color="warning"
                        disabled={!acknowledged}
                        data-testid="conflict-dialog-load-both"
                    >
                        <FormattedMessage id="patientConflict.loadBoth" />
                    </Button>
                )}
                {singleIncoming && (
                    <Button
                        onClick={() => onAction('switch')}
                        color="error"
                        disabled={!acknowledged}
                        data-testid="conflict-dialog-switch"
                    >
                        <FormattedMessage id="patientConflict.switchPatient" />
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
